import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Box } from '@pets-ui/Box/Box'
import { ButtonBase } from '@pets-ui/ButtonBase/ButtonBase'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowLeftLarge, ArrowRightLarge } from '@pets-ui/icons'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useState, type ReactNode } from 'react'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  hideScrollBar: {
    'scrollbarWidth': 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  arrowButton: {
    borderRadius: 20,
    backgroundColor: colors.white,
    boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.16)',
    color: colors.grey600,
    display: {
      [MOBILE]: 'none',
      default: 'flex',
    },
    justifyContent: 'center',
    opacity: 0,
    pointerEvents: 'none',
    height: 30,
    width: 30,
  },
  arrowLeft: {
    position: 'absolute',
    transform: 'translateY(-50%)',
    zIndex: 1,
    left: 8,
    top: '50%',
  },
  arrowRight: {
    position: 'absolute',
    transform: 'translateY(-50%)',
    zIndex: 1,
    right: 8,
    top: '50%',
  },
  fadeIn: {
    opacity: 1,
    pointerEvents: 'auto',
  },
  arrowButtonOnMobile: {
    display: 'flex',
  },
})

interface ScrollableListProps {
  children: ReactNode
  gap?: number
  showArrowsOnMobile?: boolean
}

export const ScrollableList = (props: ScrollableListProps) => {
  const { children, gap = 4, showArrowsOnMobile = false } = props
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null)
  const [showArrows, setShowArrows] = useState({ left: false, right: false })

  const updateArrowsDisplay = useCallbackEvent(() => {
    if (containerElement) {
      setShowArrows({
        left: containerElement.scrollLeft > 0,
        right: containerElement.scrollLeft < containerElement.scrollWidth - containerElement.offsetWidth,
      })
    }
  })

  useEffect(() => {
    containerElement?.addEventListener('scroll', updateArrowsDisplay)
    updateArrowsDisplay()

    return () => {
      containerElement?.removeEventListener('scroll', updateArrowsDisplay)
    }
    // children 在 deps 中確保容器長度變動時重新計算 scrollWidth
  }, [updateArrowsDisplay, containerElement, children])

  const handleLeftClick = () => {
    if (containerElement) {
      const minLeft = Math.max(0, containerElement.scrollLeft - containerElement.offsetWidth)
      containerElement.scrollTo({ left: minLeft, behavior: 'smooth' })
    }
  }

  const handleRightClick = () => {
    if (containerElement) {
      const maxLeft = Math.min(
        containerElement.scrollLeft + containerElement.offsetWidth,
        containerElement.scrollWidth - containerElement.offsetWidth,
      )
      containerElement.scrollTo({ left: maxLeft, behavior: 'smooth' })
    }
  }

  return (
    <Box position="relative">
      <ButtonBase
        rootStyle={[styles.arrowButton, showArrowsOnMobile && styles.arrowButtonOnMobile, styles.arrowLeft, showArrows.left && styles.fadeIn]}
        onClick={handleLeftClick}
      >
        <ArrowLeftLarge fontSize={16} />
      </ButtonBase>
      <HStack
        rootRef={setContainerElement}
        gap={gap}
        flexWrap="nowrap"
        overflowX="auto"
        alignItems="center"
        rootStyle={styles.hideScrollBar}
        py={1}
      >
        {children}
      </HStack>
      <ButtonBase
        rootStyle={[styles.arrowButton, showArrowsOnMobile && styles.arrowButtonOnMobile, styles.arrowRight, showArrows.right && styles.fadeIn]}
        onClick={handleRightClick}
      >
        <ArrowRightLarge fontSize={16} />
      </ButtonBase>
    </Box>
  )
}
